import {
  PreviewEmptyState,
  PreviewExclusionReason,
} from '../../../data-models/round-creation-settings.data-model';

/**
 * Every reason the API can return, in words a client understands. The API's enum is the contract;
 * this is the only place it becomes English, so an unlabelled code can never reach the screen.
 */
/** The same six reasons in two or three words, for a chip that has to sit on one line. */
export function exclusionReasonChip(reason: PreviewExclusionReason, roundLabel: string): string {
  const lower = roundLabel.toLowerCase();
  switch (reason) {
    case 'assignment-disabled':
      return 'attaching is off';
    case 'conversions-do-not-create':
      return 'conversions do not create';
    case 'not-round-eligible':
      return `not ${lower} material`;
    case 'fund-level':
      return 'fund-level';
    case 'pendingAssignment':
      return 'within grace';
    case 'unassigned':
      return `no ${lower} in window`;
    default:
      return reason;
  }
}

export function exclusionReasonLabel(reason: PreviewExclusionReason, roundLabel: string): string {
  const lower = roundLabel.toLowerCase();
  switch (reason) {
    case 'assignment-disabled':
      return `Attaching transactions to ${lower}s is switched off for this client`;
    case 'conversions-do-not-create':
      return `Conversions do not create a ${lower} under these settings`;
    case 'not-round-eligible':
      return `Not ${lower} material — fees, distributions and the like`;
    case 'fund-level':
      return `Fund-level, so it belongs to no single ${lower}`;
    case 'pendingAssignment':
      return `Found no ${lower} yet, still inside the grace period`;
    case 'unassigned':
      return `Found no ${lower} within the window`;
    default:
      return reason;
  }
}

export function emptyStateLabel(state: PreviewEmptyState, roundLabel: string): string {
  const lower = roundLabel.toLowerCase();
  switch (state) {
    case 'noEligibleTransactions':
      return `This company has no transactions these rules can group into a ${lower}.`;
    case 'creationDisabled':
      return `Creating ${lower}s from the ledger is switched off for this client, so these rules would mint nothing.`;
    case 'assignmentDisabled':
      return `Attaching transactions to ${lower}s is switched off for this client, so every transaction is left out.`;
    case 'companyNotFound':
      return 'That company could not be found.';
    default:
      return state;
  }
}

/** Everything the preview modal says, in one place so the table and its chips stay consistent. */
export function previewCopy(roundLabel: string) {
  const lower = roundLabel.toLowerCase();
  const plural = `${lower}s`;
  return {
    title: `Preview ${lower} creation`,
    subtitle: `Reruns ${lower} logic against this company with the draft settings. Nothing is saved.`,
    companyPlaceholder: 'Search a portfolio company',
    pickCompany: `Pick a company the client knows well, and this replans it from scratch under the settings above.`,
    settingsInvalid: 'Finish the settings above and the preview will run.',
    failed: 'The preview could not be run',
    close: 'Close',
    done: 'Done',
    total: 'Total',
    unnamed: 'Unnamed',
    unknownType: 'Transaction',
    unknownPosition: 'Unnamed position',
    sourceKept: 'Kept',
    sourceNew: 'New',
    notTracked: `Not ${lower}-tracked under these settings`,
    nothingSkipped: `Every transaction creates or joins a ${lower} under these settings.`,
    roundCount: (count: number, formatted: string) => `${formatted} ${count === 1 ? lower : plural}`,
    matches: (count: string) => `matches current behavior (${count})`,
    delta: (signed: string, count: string) => `${signed} vs current behavior (${count})`,
    skippedCount: (count: string) => `${count} left out`,
    trackedLabel: 'round-tracked',
    removedTitle: `${plural} that would disappear`,
    removedNone: `Every ${lower} that exists today survives these rules.`,
    removedHelp: `These exist today and have no counterpart under the drafted rules. Where a ${lower} holds transactions, they are named with the ${lower} that takes them.`,
    holdsNothing: 'holds no transactions',
    /** One destination reads as a merge; several means the members split across rounds. */
    mergesInto: (names: string[]) =>
      names.length === 1 ? `merges into ${names[0]}` : `splits across ${names.join(', ')}`,
    everythingLeftOut: `its transactions find no ${lower}`,
    txnCount: (count: number, formatted: string) => `${formatted} txn${count === 1 ? '' : 's'}`,
    transactionCount: (count: string) => `${count} txn`,
    trackedOf: (tracked: string, total: string) => `${tracked} of ${total} transactions ${lower}-tracked`,
    expand: 'Show the transactions',
    collapse: 'Hide the transactions',
    attached: 'attached',
    attachedHelp: `Joined this ${lower} under the assignment rule; it did not create it.`,
    recap: (parts: string[]) => parts.join(' · '),
    fromScratch: (company: string | null) =>
      `${company ? `${company}, replanned` : 'Replanned'} from nothing, so the count answers "how many ${plural} would these rules give me".`,
    memberColumns: {
      date: 'Date',
      type: 'Type',
      security: 'Security',
      fund: 'Fund',
      amount: 'Amount',
      shares: 'Shares',
    },
    columns: {
      source: 'Source',
      roundDate: `${roundLabel} date`,
      round: roundLabel,
      stage: 'Stage',
      raise: 'Raise amount',
      investment: 'Investment amount',
      shares: 'Shares',
    },
  };
}
